import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Table, Badge, Spinner, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPlus, faTrash, faRotate } from '@fortawesome/free-solid-svg-icons';
import { useDrums } from '../../features/drums/hooks/useDrums';
import { getDrumCapacityMeters } from '../../helpers/drumCapacity';
import { formatProductLabel } from '../../helpers/productLabel';
import { blrKg, blrLabel } from '../../helpers/blr';
import { API } from '../../config/api';
import { formatMass, exactMass } from '../../helpers/formatMass';

const STANDARD_API = `${API.BACKEND}/api/v1/standard`;
const JOBS_API = `${API.REDIS}/api/redis/jobs`;

const toNum = v => {
  if (v == null || v === '') return 0;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const fmt2 = v => (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : '—');
const upper = v => String(v ?? '').trim().toUpperCase();

const COLOR_OPTIONS = ['WHITE', 'BLACK', 'GREY', 'GREY 7004', 'BLUE', 'BROWN'];

// production order + English labels
const PHASE_ORDER = [
  'unwinding',
  'pre_stranding',
  'conductor_isolation',
  'rope',
  'interior_sheat',
  'armouring',
  'exterior_sheat',
  'coiling',
  'rewinding'
];
const PHASE_LABELS = {
  unwinding: 'Unwinding',
  pre_stranding: 'Pre-stranding',
  conductor_isolation: 'Conductor isolation',
  rope: 'Stranding (rope)',
  interior_sheat: 'Inner sheath',
  armouring: 'Armouring',
  exterior_sheat: 'Outer sheath',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};
// copper is billed once, on the first of these that the product uses (copper-once)
const COPPER_PHASES = ['unwinding', 'pre_stranding', 'conductor_isolation'];
// each conductor is wound on its own reel
const PER_CONDUCTOR = new Set(['conductor_isolation']);

const sortPhases = keys =>
  [...keys].sort((a, b) => PHASE_ORDER.indexOf(a) - PHASE_ORDER.indexOf(b));

// wound-output diameter of a phase (snapshot phase from job.phasePlan or std.phases)
function phaseOutputDiameter(phase) {
  if (!phase) return 0;
  if (phase.name === 'conductor_isolation') {
    const c = Array.isArray(phase.conductors) ? phase.conductors[0] : null;
    return (
      Number(c?.isolation?.diameter) ||
      Number(phase?.isolation?.diameter) ||
      Number(phase?.diameter) ||
      0
    );
  }
  return Number(phase.diameter) || 0;
}

// ── material per phase, straight from the job's material plan (copper-once) ──
function materialsByPhaseFromJob(job) {
  const order = (job.phasePlan || []).map(p => p.name);
  const copperPhase = COPPER_PHASES.find(k => order.includes(k)) || 'conductor_isolation';
  const map = {};
  const push = (key, line) => {
    (map[key] = map[key] || []).push(line);
  };
  for (const m of job.materialPlanSummary || []) {
    const kg = toNum(m.planned_kg ?? m.executed_kg ?? m.kg);
    if (!(kg > 0)) continue;
    if (upper(m.material) === 'COPPER') {
      const dia = m.wireDiameter ? ` Ø${fmt2(m.wireDiameter)}` : '';
      push(copperPhase, { group: 'COPPER', label: `Copper${dia}`, kg });
    } else if (upper(m.material) === 'BLR' || upper(m.material) === 'STEEL' || m.layer === 'armouring') {
      push('armouring', { group: 'BLR', label: blrLabel(m), kg });
    } else if (m.layer === 'interior_sheat') {
      push('interior_sheat', { group: 'PVC', label: `PVC ${m.grade || ''} inner sheath`.replace(/\s+/g, ' ').trim(), kg });
    } else if (m.layer === 'exterior_sheat') {
      push('exterior_sheat', { group: 'PVC', label: `PVC ${m.grade || ''} outer sheath`.replace(/\s+/g, ' ').trim(), kg });
    } else {
      // PVC insulation (no layer, carries color)
      const c = m.color ? ` (${m.color})` : '';
      push('conductor_isolation', { group: 'PVC', label: `PVC ${m.grade || ''}${c} insulation`.replace(/\s+/g, ' ').trim(), kg });
    }
  }
  return map;
}

// fallback material per phase for MANUAL products (no job): from the standard
function materialsByPhaseFromStandard(std, len) {
  const phases = std.phases || [];
  const order = phases.map(p => p.name);
  const copperPhase = COPPER_PHASES.find(k => order.includes(k)) || 'conductor_isolation';
  const map = {};
  const push = (key, line) => {
    (map[key] = map[key] || []).push(line);
  };
  const wireKgPerM = (d, density) =>
    (Math.PI * toNum(d) * toNum(d)) / 4 * 1e-6 * (toNum(density) || 8.96) * 1000;

  const ci = phases.find(p => p.name === 'conductor_isolation');
  const conductors = Array.isArray(ci?.conductors) ? ci.conductors : ci?.wire ? [{ wire: ci.wire, isolation: ci.isolation }] : [];

  // copper (billed on copperPhase) — computed from the conductor wires
  let copperKg = 0;
  for (const cond of conductors) {
    const w = cond?.wire;
    if (!w) continue;
    const density = toNum(w.density) || 8.96;
    if (upper(w.type) === 'SINGLE-WIRE') copperKg += wireKgPerM(w.diameter, density) * len;
    else {
      const diams = Array.isArray(w.wireDiameter) ? w.wireDiameter : [w.wireDiameter];
      const counts = Array.isArray(w.numberOfWires) ? w.numberOfWires : [w.numberOfWires];
      diams.forEach((d, i) => { copperKg += wireKgPerM(d, density) * len * (toNum(counts[i]) || 1); });
    }
  }
  if (copperKg > 0) push(copperPhase, { group: 'COPPER', label: 'Copper', kg: copperKg });

  // PVC insulation per conductor
  for (const cond of conductors) {
    const iso = cond?.isolation;
    if (iso?.fillingSection && iso?.density) {
      push('conductor_isolation', {
        group: 'PVC',
        label: `PVC ${iso.type || ''}${iso.color ? ` (${iso.color})` : ''} insulation`.replace(/\s+/g, ' ').trim(),
        kg: (toNum(iso.fillingSection) * toNum(iso.density) * len) / 1000
      });
    }
  }
  const intS = phases.find(p => p.name === 'interior_sheat');
  if (intS?.fillingSection && intS?.density)
    push('interior_sheat', { group: 'PVC', label: `PVC ${intS.type || ''} inner sheath`.replace(/\s+/g, ' ').trim(), kg: (toNum(intS.fillingSection) * toNum(intS.density) * len) / 1000 });
  const extS = phases.find(p => p.name === 'exterior_sheat');
  if (extS?.fillingSection && extS?.density)
    push('exterior_sheat', { group: 'PVC', label: `PVC ${extS.type || ''} outer sheath`.replace(/\s+/g, ' ').trim(), kg: (toNum(extS.fillingSection) * toNum(extS.density) * len) / 1000 });
  const arm = phases.find(p => p.name === 'armouring');
  if (arm?.wide && arm?.thickness)
    push('armouring', { group: 'BLR', label: blrLabel(arm), kg: blrKg(arm.wide, arm.thickness, arm.density, len) });

  return map;
}

function mostCommonDrum(tokens) {
  const counts = {};
  for (const t of tokens) {
    if (!t.drum_material || !t.drum_diameter) continue;
    const k = `${String(t.drum_material).toLowerCase()}|${Number(t.drum_diameter)}`;
    counts[k] = (counts[k] || 0) + 1;
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  if (!best) return null;
  const [material, diameter] = best[0].split('|');
  return { material, diameter: Number(diameter) };
}

// build the per-phase rows for one product
function buildPhaseRows({ phases, matByPhase, tokensByPhase, len, numberOfConductors, defaultReel, overrides, drumLibrary }) {
  const present = sortPhases(phases.map(p => p.name));
  return present.map(key => {
    const phase = phases.find(p => p.name === key);
    const dia = phaseOutputDiameter(phase);
    const mats = matByPhase[key] || [];
    const toks = tokensByPhase[key] || [];

    if (toks.length) {
      // phase already planned → show the REAL reels
      const drum = mostCommonDrum(toks) || defaultReel;
      return {
        key,
        label: PHASE_LABELS[key] || key,
        dia,
        mats,
        planned: true,
        reel: drum,
        reelCount: toks.length,
        capacity: null
      };
    }

    // not planned yet → estimate with the chosen/default reel
    const reel = overrides[key] || defaultReel || { material: '', diameter: '' };
    const cap =
      reel.material && reel.diameter && dia > 0
        ? getDrumCapacityMeters({ drumMaterial: reel.material, drumDiameter: reel.diameter, cableDiameter: dia, drumLibrary })
        : null;
    const lines = PER_CONDUCTOR.has(key) ? Number(numberOfConductors) || 1 : 1;
    const reelCount = cap && len > 0 ? lines * Math.ceil(len / cap) : null;
    return { key, label: PHASE_LABELS[key] || key, dia, mats, planned: false, reel, reelCount, capacity: cap, lines };
  });
}

function totalsForRows(phaseRows) {
  let copperKg = 0;
  let pvcKg = 0;
  let steelKg = 0;
  for (const pr of phaseRows) {
    for (const m of pr.mats) {
      if (m.group === 'COPPER') copperKg += m.kg;
      else if (m.group === 'BLR') steelKg += m.kg;
      else pvcKg += m.kg;
    }
  }
  return { copperKg, pvcKg, steelKg };
}

function matchStd(list, r) {
  return (
    (list || []).find(
      s =>
        upper(s.name) === upper(r.name) &&
        Number(s.numberOfConductors) === Number(r.numberOfConductors) &&
        Number(s.section) === Number(r.section) &&
        upper(s.color) === upper(r.color) &&
        (r.mixed ? Number(s.greenYellowSection || 0) === Number(r.greenYellowSection || 0) : true)
    ) || null
  );
}

function queueRowFromGroup(g) {
  const firstTask = (g.queue || [])[0] || {};
  return {
    id: `q-${g.jobId}`,
    source: 'queue',
    jobId: g.jobId,
    standardId: g.standardId || null,
    name: g.name,
    numberOfConductors: g.numberOfConductors,
    section: g.section,
    greenYellowSection: g.greenYellowSection ?? null,
    mixed: !!g.hasMixedConductorSections,
    productLabel: g.productLabel || formatProductLabel(g),
    color: null,
    include: true,
    length: toNum(g.plannedLength), // the LOT length (fixes 3× over-count)
    defaultReel: firstTask.drum_material
      ? { material: String(firstTask.drum_material).toLowerCase(), diameter: Number(firstTask.drum_diameter) || '' }
      : null,
    overrides: {},
    phaseRows: null,
    totals: null,
    error: null
  };
}

const MachineConsumablesSimulatorPanel = ({ groups = [] }) => {
  const drumsQuery = useDrums();
  const drumLibrary = drumsQuery.data?.data || [];
  const drumMaterials = useMemo(
    () => [...new Set(drumLibrary.map(d => String(d.drumType).toLowerCase()))],
    [drumLibrary]
  );
  const diametersFor = mat =>
    [
      ...new Set(
        drumLibrary
          .filter(d => String(d.drumType).toLowerCase() === String(mat).toLowerCase())
          .map(d => Number(d.drumDiameter))
      )
    ].sort((a, b) => a - b);
  const fallbackReel = () => {
    const metal = drumLibrary.filter(d => String(d.drumType).toLowerCase() === 'metal').sort((a, b) => b.drumDiameter - a.drumDiameter);
    const pick = metal[0] || drumLibrary[0];
    return pick ? { material: String(pick.drumType).toLowerCase(), diameter: Number(pick.drumDiameter) } : { material: '', diameter: '' };
  };

  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({
    name: '', numberOfConductors: '', section: '', color: 'BLACK',
    greenYellowSection: '', length: '', drumMaterial: '', drumDiameter: ''
  });

  useEffect(() => {
    setRows(prev => (prev.length ? prev : groups.map(queueRowFromGroup)));
  }, [groups]);

  const removeRow = id => setRows(prev => prev.filter(r => r.id !== id));
  const reloadFromQueue = () => setRows(groups.map(queueRowFromGroup));

  // recompute one row from its cached raw data (job or std) — for live edits.
  // Manual rows (with _std) rescale material with the edited length; queue rows
  // keep the job's plan (fixed lot length).
  const recomputeRow = row => {
    if (!row._phases) return row;
    const matByPhase = row._std
      ? materialsByPhaseFromStandard(row._std, toNum(row.length))
      : row._matByPhase;
    const phaseRows = buildPhaseRows({
      phases: row._phases,
      matByPhase,
      tokensByPhase: row._tokensByPhase || {},
      len: toNum(row.length),
      numberOfConductors: row.numberOfConductors,
      defaultReel: row.defaultReel || fallbackReel(),
      overrides: row.overrides || {},
      drumLibrary
    });
    return { ...row, _matByPhase: matByPhase, phaseRows, totals: totalsForRows(phaseRows) };
  };

  const setPhaseReel = (rowId, phaseKey, patch) =>
    setRows(prev =>
      prev.map(r => {
        if (r.id !== rowId) return r;
        const overrides = { ...(r.overrides || {}), [phaseKey]: { ...(r.overrides?.[phaseKey] || {}), ...patch } };
        return recomputeRow({ ...r, overrides });
      })
    );

  const setManualLength = (rowId, value) =>
    setRows(prev => prev.map(r => (r.id === rowId ? recomputeRow({ ...r, length: value }) : r)));

  const addManual = () => {
    if (!manual.name || !manual.numberOfConductors || !manual.section || !manual.color) return;
    setRows(prev => [
      ...prev,
      {
        id: `m-${prev.length}-${upper(manual.name)}-${toNum(manual.section)}`,
        source: 'manual',
        standardId: null,
        name: upper(manual.name),
        numberOfConductors: toNum(manual.numberOfConductors),
        section: toNum(manual.section),
        greenYellowSection: manual.greenYellowSection ? toNum(manual.greenYellowSection) : null,
        mixed: !!manual.greenYellowSection,
        productLabel: formatProductLabel({
          name: upper(manual.name),
          numberOfConductors: toNum(manual.numberOfConductors),
          section: toNum(manual.section),
          greenYellowSection: manual.greenYellowSection ? toNum(manual.greenYellowSection) : null,
        }),
        color: upper(manual.color),
        include: true,
        length: toNum(manual.length),
        defaultReel:
          manual.drumMaterial && manual.drumDiameter
            ? { material: manual.drumMaterial, diameter: Number(manual.drumDiameter) }
            : null,
        overrides: {},
        phaseRows: null,
        totals: null,
        error: null
      }
    ]);
    setManual(m => ({ ...m, name: '', numberOfConductors: '', section: '', length: '' }));
  };

  const simulate = async () => {
    setRunning(true);
    try {
      const jobCache = {};
      const next = await Promise.all(
        rows.map(async r => {
          if (!r.include) return { ...r, phaseRows: null };
          try {
            if (r.source === 'queue') {
              if (!r.jobId) return { ...r, error: 'No jobId', phaseRows: null };
              if (!jobCache[r.jobId]) {
                const res = await axios.get(`${JOBS_API}/${r.jobId}`);
                jobCache[r.jobId] = res.data?.job || res.data || null;
              }
              const job = jobCache[r.jobId];
              if (!job?.phasePlan) return { ...r, error: 'Job data unavailable', phaseRows: null };
              const tokensByPhase = {};
              for (const t of Object.values(job.tokens || {})) {
                (tokensByPhase[t.phase] = tokensByPhase[t.phase] || []).push(t);
              }
              const base = {
                ...r,
                error: null,
                length: toNum(job.plannedLength) || toNum(r.length),
                numberOfConductors: job.numberOfConductors ?? r.numberOfConductors,
                color: job.color || r.color,
                defaultReel: r.defaultReel || fallbackReel(),
                _phases: job.phasePlan,
                _matByPhase: materialsByPhaseFromJob(job),
                _tokensByPhase: tokensByPhase
              };
              return recomputeRow(base);
            }
            // manual → resolve standard
            const res = await axios.post(`${STANDARD_API}/find-many`, [
              {
                name: r.name,
                numberOfConductors: r.numberOfConductors,
                section: r.section,
                color: r.color,
                hasMixedConductorSections: r.mixed,
                greenYellowSection: r.greenYellowSection
              }
            ]);
            const std = matchStd(res.data?.data || [], r);
            if (!std?.phases) return { ...r, error: 'Standard not found', phaseRows: null };
            const base = {
              ...r,
              error: null,
              color: std.color || r.color,
              defaultReel: r.defaultReel || fallbackReel(),
              _phases: std.phases,
              _std: std,
              _matByPhase: materialsByPhaseFromStandard(std, toNum(r.length)),
              _tokensByPhase: {}
            };
            return recomputeRow(base);
          } catch (e) {
            return { ...r, error: 'Fetch failed', phaseRows: null };
          }
        })
      );
      setRows(next);
    } finally {
      setRunning(false);
    }
  };

  // grand totals across included products
  const grand = useMemo(() => {
    let copperKg = 0;
    let pvcKg = 0;
    let steelKg = 0;
    const reelAgg = {}; // material|Ø -> {material, diameter, count, estimated}
    for (const r of rows) {
      if (!r.include || !r.phaseRows) continue;
      copperKg += r.totals?.copperKg || 0;
      pvcKg += r.totals?.pvcKg || 0;
      steelKg += r.totals?.steelKg || 0;
      for (const pr of r.phaseRows) {
        if (!pr.reelCount || !pr.reel?.material || !pr.reel?.diameter) continue;
        const k = `${pr.reel.material}|${pr.reel.diameter}`;
        reelAgg[k] = reelAgg[k] || { material: pr.reel.material, diameter: pr.reel.diameter, count: 0, estimated: false };
        reelAgg[k].count += pr.reelCount;
        if (!pr.planned) reelAgg[k].estimated = true;
      }
    }
    const reelLines = Object.values(reelAgg).sort(
      (a, b) => String(a.material).localeCompare(b.material) || a.diameter - b.diameter
    );
    return { copperKg, pvcKg, steelKg, reelLines, totalReels: reelLines.reduce((s, d) => s + d.count, 0) };
  }, [rows]);

  const includedCount = rows.filter(r => r.include).length;
  const hasResults = rows.some(r => r.include && r.phaseRows);

  return (
    <div className="py-4">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h4 className="mb-1">Consumables per phase</h4>
          <div className="text-700 fs-9">
            What each phase consumes (copper counted once) and how many reels — from the
            actual job plan, like project “material execution”.
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={reloadFromQueue} title="Reload the queue">
            <FontAwesomeIcon icon={faRotate} className="me-1" /> Reload queue
          </Button>
          <Button variant="primary" onClick={simulate} disabled={running || !includedCount}>
            {running ? <Spinner size="sm" animation="border" className="me-2" /> : <FontAwesomeIcon icon={faPlay} className="me-2" />}
            Simulate
          </Button>
        </div>
      </div>

      {rows.length === 0 && (
        <div className="text-700 py-3">Queue is empty. Add a manual product below.</div>
      )}

      {/* per-product cards */}
      {rows.map(r => (
        <Card key={r.id} className="mb-3">
          <Card.Header className="py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <Form.Check
                checked={r.include}
                onChange={e => setRows(prev => prev.map(x => (x.id === r.id ? { ...x, include: e.target.checked } : x)))}
              />
              <span className="fw-bold">{r.productLabel}</span>
              {r.mixed && r.greenYellowSection != null && <span className="text-700">+{r.greenYellowSection}</span>}
              {r.color && <Badge bg="secondary">{r.color}</Badge>}
              {r.source === 'manual' && <Badge bg="info">manual</Badge>}
            </div>
            <div className="d-flex align-items-center gap-3">
              <span className="fs-9">
                Lot:{' '}
                {r.source === 'manual' ? (
                  <Form.Control
                    size="sm"
                    className="d-inline-block no-spin text-end"
                    style={{ width: 90 }}
                    value={r.length}
                    onChange={e => setManualLength(r.id, e.target.value)}
                  />
                ) : (
                  <b>{fmt2(r.length)}</b>
                )}{' '}
                m
              </span>
              {r.totals && (
                <span className="fs-9 text-700">
                  Cu <b>{fmt2(r.totals.copperKg)}</b> kg · PVC <b>{fmt2(r.totals.pvcKg)}</b> kg
                  {r.totals.steelKg > 0 && <> · BLR <b>{fmt2(r.totals.steelKg)}</b> kg</>}
                </span>
              )}
              {r.source === 'manual' && (
                <Button variant="link" size="sm" className="text-danger p-0" onClick={() => removeRow(r.id)}>
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              )}
            </div>
          </Card.Header>
          {r.error && <Card.Body className="py-2 text-danger fs-9">{r.error}</Card.Body>}
          {r.phaseRows && (
            <div className="table-responsive">
              <Table size="sm" className="fs-9 mb-0 align-middle">
                <thead>
                  <tr className="text-700">
                    <th>Phase</th>
                    <th>Consumes</th>
                    <th className="text-end">Output Ø</th>
                    <th>Reel</th>
                    <th className="text-end">Reels</th>
                  </tr>
                </thead>
                <tbody>
                  {r.phaseRows.map(pr => (
                    <tr key={pr.key}>
                      <td className="fw-semibold white-space-nowrap">{pr.label}</td>
                      <td>
                        {pr.mats.length === 0 ? (
                          <span className="text-700">— physical reel only</span>
                        ) : (
                          pr.mats.map((m, i) => (
                            <div key={i} className="white-space-nowrap">
                              <Badge
                                bg={m.group === 'COPPER' ? 'warning' : m.group === 'BLR' ? 'secondary' : 'info'}
                                className="me-2"
                              >
                                {m.group}
                              </Badge>
                              {m.label} <b>{fmt2(m.kg)} kg</b>
                            </div>
                          ))
                        )}
                      </td>
                      <td className="text-end white-space-nowrap">{pr.dia ? `${fmt2(pr.dia)} mm` : '—'}</td>
                      <td style={{ minWidth: 180 }}>
                        {pr.planned ? (
                          <span className="text-capitalize">
                            {pr.reel?.material} Ø{pr.reel?.diameter}{' '}
                            <Badge bg="success" className="ms-1">planned</Badge>
                          </span>
                        ) : (
                          <div className="d-flex gap-1">
                            <Form.Select
                              size="sm"
                              value={pr.reel?.material || ''}
                              onChange={e =>
                                setPhaseReel(r.id, pr.key, {
                                  material: e.target.value,
                                  diameter: diametersFor(e.target.value)[0] || ''
                                })
                              }
                            >
                              <option value="">type…</option>
                              {drumMaterials.map(m => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </Form.Select>
                            <Form.Select
                              size="sm"
                              value={pr.reel?.diameter || ''}
                              onChange={e => setPhaseReel(r.id, pr.key, { diameter: Number(e.target.value) })}
                              disabled={!pr.reel?.material}
                            >
                              <option value="">Ø…</option>
                              {diametersFor(pr.reel?.material).map(d => (
                                <option key={d} value={d}>{d}</option>
                              ))}
                            </Form.Select>
                          </div>
                        )}
                      </td>
                      <td className="text-end fw-bold">
                        {pr.reelCount != null ? pr.reelCount : '—'}
                        {!pr.planned && pr.reelCount != null && (
                          <div className="text-700 fs-10 fw-normal">est.</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card>
      ))}

      {/* add manual product */}
      <div className="mb-4">
        <Button variant="link" size="sm" className="p-0" onClick={() => setShowManual(s => !s)}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          {showManual ? 'Hide manual product' : 'Add manual product'}
        </Button>
        {showManual && (
          <Card className="mt-2">
            <Card.Body className="py-3">
              <Row className="g-2 align-items-end">
                <Col xs={6} md={2}>
                  <Form.Label className="fs-10 mb-0">Name</Form.Label>
                  <Form.Control size="sm" value={manual.name} placeholder="CYYF" onChange={e => setManual({ ...manual, name: e.target.value })} />
                </Col>
                <Col xs={3} md={1}>
                  <Form.Label className="fs-10 mb-0">Cond.</Form.Label>
                  <Form.Control size="sm" className="no-spin" value={manual.numberOfConductors} onChange={e => setManual({ ...manual, numberOfConductors: e.target.value })} />
                </Col>
                <Col xs={3} md={1}>
                  <Form.Label className="fs-10 mb-0">Sect.</Form.Label>
                  <Form.Control size="sm" className="no-spin" value={manual.section} onChange={e => setManual({ ...manual, section: e.target.value })} />
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label className="fs-10 mb-0">Color</Form.Label>
                  <Form.Select size="sm" value={manual.color} onChange={e => setManual({ ...manual, color: e.target.value })}>
                    {COLOR_OPTIONS.map(c => (<option key={c} value={c}>{c}</option>))}
                  </Form.Select>
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label className="fs-10 mb-0">Length (m)</Form.Label>
                  <Form.Control size="sm" className="no-spin" value={manual.length} onChange={e => setManual({ ...manual, length: e.target.value })} />
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label className="fs-10 mb-0">Default reel</Form.Label>
                  <div className="d-flex gap-1">
                    <Form.Select size="sm" value={manual.drumMaterial} onChange={e => setManual({ ...manual, drumMaterial: e.target.value, drumDiameter: diametersFor(e.target.value)[0] || '' })}>
                      <option value="">type…</option>
                      {drumMaterials.map(m => (<option key={m} value={m}>{m}</option>))}
                    </Form.Select>
                    <Form.Select size="sm" value={manual.drumDiameter} onChange={e => setManual({ ...manual, drumDiameter: e.target.value })} disabled={!manual.drumMaterial}>
                      <option value="">Ø…</option>
                      {diametersFor(manual.drumMaterial).map(d => (<option key={d} value={d}>{d}</option>))}
                    </Form.Select>
                  </div>
                </Col>
                <Col xs={6} md={2}>
                  <Button variant="outline-primary" size="sm" onClick={addManual}>
                    <FontAwesomeIcon icon={faPlus} className="me-1" /> Add
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </div>

      {/* grand totals */}
      {hasResults && (
        <Row className="g-3">
          <Col md={6}>
            <Card className="h-100">
              <Card.Header className="py-2 fw-bold">Total materials</Card.Header>
              <Card.Body className="py-2 d-flex gap-4 flex-wrap">
                <div>
                  <div className="text-700 fs-10">Copper</div>
                  <div className="fs-5 fw-bold" title={exactMass(grand.copperKg)}>
                    {formatMass(grand.copperKg)}
                  </div>
                </div>
                <div>
                  <div className="text-700 fs-10">PVC / compound</div>
                  <div className="fs-5 fw-bold" title={exactMass(grand.pvcKg)}>
                    {formatMass(grand.pvcKg)}
                  </div>
                </div>
                {grand.steelKg > 0 && (
                  <div>
                    <div className="text-700 fs-10">BLR</div>
                    <div className="fs-5 fw-bold" title={exactMass(grand.steelKg)}>
                    {formatMass(grand.steelKg)}
                  </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="h-100">
              <Card.Header className="py-2 fw-bold d-flex justify-content-between">
                <span>Total reels</span>
                <span className="fs-9">{grand.totalReels} pcs</span>
              </Card.Header>
              <Card.Body className="py-2">
                {grand.reelLines.length === 0 ? (
                  <div className="text-700 fs-9">Set a reel per unplanned phase.</div>
                ) : (
                  <Table size="sm" className="fs-9 mb-0">
                    <thead>
                      <tr className="text-700">
                        <th>Type</th>
                        <th className="text-end">Reel Ø</th>
                        <th className="text-end">Pcs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grand.reelLines.map(d => (
                        <tr key={`${d.material}-${d.diameter}`}>
                          <td className="text-capitalize">{d.material}</td>
                          <td className="text-end">{d.diameter}</td>
                          <td className="text-end fw-bold">
                            {d.count}
                            {d.estimated && <span className="text-700 fw-normal"> *</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
                {grand.reelLines.some(d => d.estimated) && (
                  <div className="text-700 fs-10 mt-1">* includes estimated (not-yet-planned) phases</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default MachineConsumablesSimulatorPanel;
